/** * 新規メッセージ送信コンポーネント */
<template>
  <ifield-b-modal
    id="message-post-dialog"
    ref="message-post-dialog-modal"
    v-bind:title="modalTitle"
    centered
    scrollable
    no-body-border
    size="lg"
    ok-variant="success"
    ignore-enforce-focus-selector=".popover"
    v-on:close="hideModal"
    v-on:hidden="$_clearForm"
  >
    <message-post
      v-show="!isSchedule"
      ref="message-post"
      v-bind:default-receive-users="defaultReceiveUserIds"
      v-bind="$attrs"
      v-on:message-post-end="$_onMessagePostEnd"
    />
    <schedule-editor
      v-if="canSetTask"
      v-show="isSchedule"
      ref="schedule-editor"
      v-bind:domainName="$t('appMessagePostDialog.task')"
      sendMessage
      v-on:schedule-editend="$_onScheduleEditEnd"
    />
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
    <!-- modal footer -->
    <template v-slot:modal-footer>
      <!-- キャンセルボタン -->
      <b-button
        size="sm"
        variant="secondary"
        v-t="'appMessagePostDialog.action.cancel'"
        v-on:click="hideModal"
      />
      <template v-if="isSchedule">
        <!-- メッセージへの切替ボタン -->
        <b-button
          size="sm"
          variant="primary"
          v-t="'appMessagePostDialog.message_'"
          v-on:click="$_toggleForm"
        />
        <!-- 送信ボタン -->
        <b-button
          size="sm"
          variant="success"
          v-t="'appMessagePostDialog.action.register'"
          v-on:click="$_registerSchedule"
        />
      </template>
      <template v-else>
        <!-- 作業指示への切替ボタン -->
        <b-button
          v-if="canSetTask"
          size="sm"
          variant="primary"
          v-t="'appMessagePostDialog.task'"
          v-on:click="$_toggleForm"
        />
        <!-- 送信ボタン -->
        <b-button
          size="sm"
          variant="success"
          v-t="'appMessagePostDialog.action.send'"
          v-on:click="$_sendMessage"
        />
      </template>
    </template>
  </ifield-b-modal>
</template>

<script>
import { TAG_USER_ROLE_ID } from 'ifield-core-components'
import { FUNCTION_TYPE } from '@/constants.js'
import MessagePost from '@/components/message/AppMessagePost.vue'
import ScheduleEditor from '@/components/schedule/AppScheduleEditor.vue'

export default {
  name: 'AppMessagePostDialog',
  components: {
    'message-post': MessagePost,
    'schedule-editor': ScheduleEditor,
  },
  inheritAttrs: false,
  props: {
    /**
     * 初期宛先(対象)ユーザー
     * Array.<{ id: Number, role_id: Number}>で指定する
     */
    defaultReceiveUsers: {
      type: Array,
      default: () => [],
    },
  },
  data: function () {
    return {
      // 作業指示フォームを表示する場合はtrue
      isSchedule: false,
      // ロード中フラグ
      isLoading: false,
    }
  },
  computed: {
    /**
     * ログインユーザーが管理者の場合はtrue
     * @type {Boolean}
     */
    isManager: function () {
      return this.$store.state.login.isManager
    },
    /**
     * スケジュール機能が利用可能か？
     * @type {Boolean}
     */
    isScheduleAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SCHEDULE)
    },
    /**
     * 作業指示可能な場合はtrue
     * @type {Boolean}
     */
    canSetTask: function () {
      return this.isManager && this.isScheduleAvailable
    },
    /**
     * スケジュールフォームに渡す初期ユーザー
     * @type {{ user_id: number; }[]}
     */
    defaultUsersForSchedule: function () {
      return (this.defaultReceiveUserIds || []).map((id) => ({ user_id: id }))
    },
    /**
     * 宛先(対象)ユーザーID配列
     * @type {Array.<Number>}
     */
    defaultReceiveUserIds: function () {
      return this.defaultReceiveUsers.map((u) => u.id)
    },
    /**
     * モーダルのタイトル
     * @type {String}
     */
    modalTitle: function () {
      return this.isSchedule
        ? this.$t('appMessagePostDialog.task')
        : this.$t('appMessagePostDialog.message.sendMessage')
    },
  },
  mounted: function () {
    this.messageForm = this.$refs['message-post']
    if (this.canSetTask) {
      this.scheduleForm = this.$refs['schedule-editor']
    }
    this.$log.info('AppMessagePostDialog mounted.')
  },
  methods: {
    /**
     * ダイアログを表示する
     */
    showModal: async function () {
      // 対象ユーザーにタグユーザーが含まれている場合、アラートを出して終了
      if (
        this.defaultReceiveUsers.some(
          (user) => user.role_id === TAG_USER_ROLE_ID
        )
      ) {
        const tagUsers = this.defaultReceiveUsers.filter(
          (user) => user.role_id === TAG_USER_ROLE_ID
        )
        await this.$_showIncludingTagUserAlert(tagUsers)
        return
      }

      this.$_showLoading()
      this.$refs['message-post-dialog-modal'].show()
      this.$nextTick().then(async () => {
        this.messageForm = this.$refs['message-post']
        if (this.canSetTask) {
          this.scheduleForm = this.$refs['schedule-editor']
        }
        await this.$_showForm()
        this.$_hideLoading()
      })
    },
    /**
     * タグユーザーが宛先指定されているアラートを表示する
     * @param {Object[]} users 対象のユーザー
     */
    $_showIncludingTagUserAlert: async function (users) {
      const userNames = users.map((u) => u.user_name).join(', ')
      const html =
        `<p class="text">${this.$t(
          'appMessagePostDialog.message.followingAreTagUsers'
        )}</p>` +
        `<p class="text users">${userNames}</p>` +
        `<p class="text">${this.$t(
          'appMessagePostDialog.message.selectOtherUsers'
        )}</p>`

      await this.$alert.fire({
        target: document.body,
        icon: 'error',
        title: this.$t('appMessagePostDialog.message.cannotSendToTagUser'),
        html,
        confirmButtonText: this.$t('appMessagePostDialog.action.ok'),
        width: 'auto',
        customClass: {
          htmlContainer: 'confirm-content',
        },
      })
    },

    /**
     * フォームを表示する
     */
    $_showForm: async function () {
      const promises = []
      promises.push(this.messageForm.show())
      if (this.canSetTask) {
        promises.push(
          this.scheduleForm.show(null, {
            targetUsers: this.defaultUsersForSchedule,
          })
        )
      }
      return await Promise.all(promises)
    },

    /**
     * ダイアログを非表示にする
     */
    hideModal: function () {
      this.$refs['message-post-dialog-modal'].hide()
    },

    /**
     * フォームをクリアする
     */
    $_clearForm: function () {
      this.messageForm.clear()
      this.canSetTask && this.scheduleForm.clear()
    },

    /**
     * フォームを切り替える
     */
    $_toggleForm: function () {
      this.$_synchronizeSelectedUsers()
      this.isSchedule = !this.isSchedule
    },

    /**
     * 選択したユーザーを同期する
     */
    $_synchronizeSelectedUsers: function () {
      if (this.isSchedule) {
        const userIds = this.scheduleForm.getCurrentTargetUsers()
        this.messageForm.setCurrentReceiveUsers(userIds)
      } else {
        const userIds = this.messageForm.getCurrentReceiveUsers()
        userIds !== false && this.scheduleForm.setCurrentTargetUsers(userIds)
      }
    },

    /**
     * メッセージを送信する
     */
    $_sendMessage: function () {
      this.messageForm.sendMessage()
    },

    /**
     * 作業指示を作成する
     */
    $_registerSchedule: function () {
      this.scheduleForm.save()
    },

    /**
     * メッセージ送信完了時の処理
     */
    $_onMessagePostEnd: function () {
      /**
       * メッセージ送信完了イベント
       */
      this.$emit('message-post-end')
      this.hideModal()
    },

    /**
     * 作業指示作成完了時の処理
     *
     * @param {Boolean} isCreate 新規登録かどうか
     */
    $_onScheduleEditEnd: function (isCreate) {
      /**
       * スケジュール保存完了後のイベント
       * @param {Boolean} isCreate 新規登録かどうか
       */
      this.$emit('schedule-editend', isCreate)
      this.hideModal()
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .confirm-content {
  color: $text-color;
  font-size: $font-size-large;

  .text {
    margin: 0;
    line-height: 1.7;

    &.users {
      max-width: 32rem;
      font-weight: $font-weight-bold;
      margin: 1rem auto;
    }
  }
}
</style>
